const BigNumber = require('bignumber.js');
const VerifoneActionNames = require('./Constants/VerifoneActionNames');
const VerifoneContinueTransactionRecord = require('./Records/Request/VerifoneContinueTransactionRecord');
const VerifoneCustomReprintMerchantReceiptRecord = require('./Records/Request/VerifoneCustomReprintMerchantReceiptRecord');
const VerifoneIntegrationType = require('./VerifoneIntegrationType');
const VerifoneLoginRequestRecord = require('./Records/Request/VerifoneLoginRequestRecord');
const VerifoneLoginResponseRecord = require('./Records/Response/VerifoneLoginResponseRecord');
const VerifoneLogoutRequestRecord = require('./Records/Request/VerifoneLogoutRequestRecord');
const VerifoneLogoutResponseRecord = require('./Records/Response/VerifoneLogoutResponseRecord');
const VerifoneSocketNames = require('./VerifoneSocketNames');
const VerifoneStatusNames = require('./Constants/VerifoneStatusNames');
const VerifoneStatusRecord = require('./Records/Response/VerifoneStatusRecord');
const VerifoneSubmitOfflineTransactionsRequestRecord = require('./Records/Request/VerifoneSubmitOfflineTransactionsRequestRecord');
const VerifoneSubmitOfflineTransactionsResponseRecord = require('./Records/Response/VerifoneSubmitOfflineTransactionsResponseRecord');
const VerifoneTransactionRequestRecord = require('./Records/Request/VerifoneTransactionRequestRecord');
const VerifoneTransactionResponseRecord = require('./Records/Response/VerifoneTransactionResponseRecord');
const VerifoneTransactionResultMessages = require('./Constants/VerifoneTransactionResultMessages');
const VerifoneVoucherRecord = require('./Records/Response/VerifoneVoucherRecord');

class VerifoneResponseRecordFactory {
    /**
     * @param {Hydrator} hydrator
     * @param {VerifoneConfig} verifoneConfig
     */
    constructor(
        hydrator,
        verifoneConfig
    ) {
        this.hydrator = hydrator;
        this.config = verifoneConfig;
    }

    /**
     * @param {object} lastRequestRecordTypeSent
     */
    getExpectedResponseRecordType(lastRequestRecordTypeSent) {
        if (lastRequestRecordTypeSent instanceof VerifoneTransactionRequestRecord) {
            return VerifoneTransactionResponseRecord;
        }
        if (lastRequestRecordTypeSent instanceof VerifoneLoginRequestRecord) {
            return VerifoneLoginResponseRecord;
        }
        if (lastRequestRecordTypeSent instanceof VerifoneLogoutRequestRecord) {
            return VerifoneLogoutResponseRecord;
        }
        if (lastRequestRecordTypeSent instanceof VerifoneSubmitOfflineTransactionsRequestRecord) {
            return VerifoneSubmitOfflineTransactionsResponseRecord;
        }

        return null;
    }

    /**
     * @param {string} dataString
     * @return {string}
     */
    cleanString(dataString) {
        // Fix a bug with reprinting receipts from a VX820IP:
        // If you reprint receipts it replies with:
        // V,***** DUPLICATE *****
        // ** CARDHOLDER COPY **, ...
        // You would expect the new line to be a comma like the rest of the new lines in the voucher but it isn't.
        // Verifone has confirmed this is a bug but they will not fix it as it would break other people's workarounds.
        dataString = dataString.replace('V,***** DUPLICATE *****\n', 'V,***** DUPLICATE *****,');

        // Remove special device control characters such as ACK
        // See: https://www.w3schools.com/charsets/ref_html_ascii.asp
        dataString = dataString.replace(/[\000-\031]/g, '');

        // Remove newlines and whitespace from beginning of string.
        dataString = dataString.replace(/^\s+/g, '');

        // Remove newlines and whitespace from end of string.
        dataString = dataString.replace(/\s+$/g, '');

        return dataString;
    }

    /**
     * @param {string} dataString
     * @param {string} [socketName] The socket (integration or progress) can give clues about what type of record it is.
     * @param {object} [lastRecordSent]
     */
    buildRecordFromString(dataString, socketName, lastRecordSent = null) {
        dataString = this.cleanString(dataString);

        if (dataString.length < 1) {
            return null;
        }

        if (socketName === VerifoneSocketNames.PROGRESS) {
            if (dataString.substr(0, 4) === '100,') {
                // This is a status record from POS Client or Engage terminal on the progress port,

                // For P400 integrations we can't just split by commas because there might be commas within the 5th
                // field (the XML receipt string)
                // But we know if it start with "100," it's a status record and it should have 5 fields.
                // This is why status records come here before all the below code.
                let stringParts = dataString.split(',');

                let recordData = stringParts.splice(0, 4); // Keep the first 4 elements.
                recordData.push(stringParts.join(',')); // Add back all remaining elements are the 5th element.
                // Add an empty first element to increment all the indices by 1 so the field numbers match
                // what's in the Verifone docs.
                recordData.unshift(null);

                let record = new VerifoneStatusRecord();
                this.hydrator.hydrate(recordData, record);
                this.enhanceStatusRecord(record);
                return record;
            }

            // We shouldn't receive anything other than strings starting with "100," on the progress socket.
            throw new Error(`Unexpected data received on progress socket: ${dataString}`);
        }

        // In getHydrationMap() of the response record models it's easier to
        // understand if the field number is the same number from the Verifone docs.
        // The docs start from field 1, but str.split() will start at 0.
        // So insert an extra comma at the start of the string before splitting so the actual
        // data starts at index 1.
        let recordData = (',' + dataString).split(',');
        recordData.rawResponse = dataString;

        if (dataString.substr(0, 4) === '100,') {
            // This is a status record on the integration socket.
            // (It must be because a status record on the progress socket has already been handled.)
            // This only happens for Evo integrations. We can't use the same code for status records as above because
            // these have the message on field 18 instead of field 3.
            let record = new VerifoneStatusRecord();
            this.hydrator.hydrate(recordData, record);
            this.enhanceStatusRecord(record);
            return record;
        }

        if (dataString.substr(0, 2) === 'V,') {
            // Voucher record received (Evo integration)
            let record = new VerifoneVoucherRecord();
            this.hydrator.hydrate(recordData, record);
            return record;
        }

        // Try to determine what type it is.
        let ExpectedRecordType = this.getExpectedResponseRecordType(lastRecordSent);
        if (ExpectedRecordType && ExpectedRecordType !== VerifoneTransactionResponseRecord) {
            let record = new ExpectedRecordType();
            this.hydrator.hydrate(recordData, record);
            return record;
        }

        // Return a generic record.
        return this.createTransactionResponse(recordData);
    }

    /**
     * @param recordData
     * @return {VerifoneTransactionResponseRecord}
     */
    createTransactionResponse(recordData) {
        const record = new VerifoneTransactionResponseRecord();
        this.hydrator.hydrate(recordData, record);

        const gratuity = new BigNumber(record.gratuityValue);
        const cashback = new BigNumber(record.cashBackValue);
        const totalTransactionValueProcessed = new BigNumber(record.totalTransactionValueProcessed);

        // We need to fix differences in the total amount field between different integrations.
        // We want:
        // record.transactionValue to be the amount ProPoint sent to the terminal, exclusive of cashback and gratuity.
        // record.totalAmount to be the total amount the customer paid, including cashback and gratuity.
        switch (this.config.integrationType) {
            case VerifoneIntegrationType.POS_CLIENT:
                // POS Client:
                // Despite what the name insists, the totalTransactionValueProcessed is not the total amount. It is only
                // the original amount that we sent. (cashback and gratuity are not included).
                // The documentation says "Field will show the total requested by the POS"

                // Use the totalTransactionValueProcessed for what it actually is.
                record.transactionValue = totalTransactionValueProcessed.toNumber();

                // Calculate totalValue.
                record.totalValue = totalTransactionValueProcessed.plus(gratuity).plus(cashback).toNumber();
                break;

            case VerifoneIntegrationType.ENGAGE:
                // Engage:
                // totalTransactionValueProcessed IS the total amount, but we have no field that tells us just the
                // original amount (excluding cashback and gratuity).
                // The documentation says "Field will show total that will be debited"

                // Calculate transactionValue.
                record.transactionValue = totalTransactionValueProcessed.minus(gratuity).minus(cashback).toNumber();

                // Use totalTransactionValueProcessed as it is.
                record.totalValue = totalTransactionValueProcessed.toNumber();
                break;

            case VerifoneIntegrationType.EVO:
                // EVO:
                // The documentation says "Field will show total that will be debited, inclusive of any DCC."
                // (DCC is currency conversion)
                // **But the documentation is wrong!**
                // The totalTransactionValueProcessed field actually returns the original
                // transaction value, just like POS Client.

                // Use totalTransactionValueProcessed for what it really is.
                record.transactionValue = totalTransactionValueProcessed.toNumber();

                // Calculate totalValue.
                record.totalValue = totalTransactionValueProcessed.plus(gratuity).plus(cashback).toNumber();
                break;
        }

        // Sometimes the terminal does not populate field 18 with a message.
        // This is at least true of the Engage terminals in the case of result 8 (communications down).
        // We know what these results mean, so populate the field if it was left empty.
        if (!record.message) {
            record.message = VerifoneTransactionResultMessages[record.result] || null;
        }

        return record;
    }

    /**
     * Add more info about the status, and generate possible actions in response.
     * This previously happened inside the status record but has been moved here so we can use the appropriate list
     * of statuses of actions from the config depending on the integration type.
     *
     * @param {VerifoneStatusRecord} record
     */
    enhanceStatusRecord(record) {
        if (record.statusId !== null) {
            // Lookup this status from the list of known statuses.
            const statusName = this.config.statuses.findNameById(record.statusId);
            if (statusName) {
                record.statusName = statusName;

                const status = this.config.statuses.findByName(statusName);

                if (status.message) {
                    // If we have a predefined message use that, because the message from the terminal
                    // usually has NoSpaces so should only be used as a fallback.
                    record.message = status.message;
                }

                record.customerFacingMessage = status.customerFacingMessage || null;
                record.icon = status.icon || null;

                record.possibleActionRecords = this.generatePossibleActionRecordsForStatus(status, statusName);
            }

            // TODO: For Evo integrations look up the status by the message text.
        }
    }

    /**
     * Return all the possible actions that can be performed for this status.
     *
     * @param {{id: number, message: string, customerFacingMessage?: string, icon?: string, actions?: string[]}} statusInfo
     * @param {string} statusName
     *
     * @return {VerifoneContinueTransactionRecord[]}
     */
    generatePossibleActionRecordsForStatus(statusInfo, statusName) {
        // Can any actions be performed for this status?
        if (!statusInfo.actions) {
            return [];
        }

        const continueRecords = [];
        statusInfo.actions.forEach((actionName) => {
            // Find the action by this name.
            const action = this.config.actions.findByName(actionName);
            if (action) {
                continueRecords.push(
                    new VerifoneContinueTransactionRecord(
                        actionName,
                        action.id,
                        action.message
                    )
                );
            }
        });

        // VerifoneStatusNames.SIGNATURE_CONFIRMATION_REQUIRED (status 18)
        // When at the "waiting for signature confirmation" prompt the operator needs to accept or reject the
        // customer's signature on the receipt. They must be able to re-print the receipt for them to sign if it gets
        // lost or damaged.
        // With POS Client an acceptable action to send at this point is REPRINT_RECEIPT (5) - makes sense.
        // With Engage you are not allowed to send REPRNT_RECEIPT at this point. Verifone acknowledge that this is dumb.
        // So we must implement our own method of reprinting the merchant receipt at this stage. VerifoneDriver
        // remembers each merchant receipt it prints so we can just call reprintLastMerchantReceipt (note that it
        // *must* say duplicate on it, but VerifoneDriver handles that.)
        // To do allow this, add an extra action type that will be intercepted and handled by VerifoneDriver instead
        // of being sent to the terminal.
        if (
            this.config.integrationType === VerifoneIntegrationType.ENGAGE
            && statusName === VerifoneStatusNames.SIGNATURE_CONFIRMATION_REQUIRED
        ) {
            continueRecords.push(
                new VerifoneCustomReprintMerchantReceiptRecord(
                    VerifoneActionNames.REPRINT_RECEIPT,
                    -999,
                    'Reprint Receipt'
                )
            );
        }

        return continueRecords;
    }
}

module.exports = VerifoneResponseRecordFactory;
